import React, { useState, useRef } from 'react';
import { View, TouchableOpacity, Image, ScrollView, ActivityIndicator, Modal, Text } from 'react-native';
import { MdAddPhotoAlternate, MdClose, MdDelete, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import ReactDOM from 'react-dom';

interface GalleryImage {
  url: string;
  key: string;
  uploaded_by: string;
  uploaded_at: string;
  itinerary_id?: string;
  caption?: string;
}

interface CardImageGalleryProps {
  cardId: string;
  currentUserId?: string | null;
  currentItineraryId?: string | null; // 用于过滤显示（行程编辑器中）
  gallery?: GalleryImage[];
  onUpdate?: (updatedGallery: GalleryImage[]) => Promise<void>;
  canUpload?: boolean;
  showAllImages?: boolean; // true: 卡片详情页显示所有，false: 行程中仅显示当前行程
  isAdmin?: boolean;
  labels?: {
    allPhotos?: string;
    tripPhotos?: string;
    addPhoto?: string;
  };
}

const CARD_IMAGE_UPLOAD_ENDPOINT = 
  (typeof window !== 'undefined' && (window as any).__CARD_IMAGE_UPLOAD__) || 
  'https://card-image-upload.cubemeet.app';

export const CardImageGallery: React.FC<CardImageGalleryProps> = ({
  cardId,
  currentUserId,
  currentItineraryId,
  gallery = [],
  onUpdate,
  canUpload = true,
  showAllImages = false,
  isAdmin = false,
  labels,
}) => {
  const [uploading, setUploading] = useState(false);
  const [lightboxState, setLightboxState] = useState<{
    images: GalleryImage[];
    currentIndex: number;
  } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 过滤显示的图片
  const displayedImages = showAllImages 
    ? gallery 
    : gallery.filter(img => !img.itinerary_id || img.itinerary_id === currentItineraryId);

  // 检查删除权限
  const canDelete = (image: GalleryImage) => {
    if (!currentUserId) return false;
    return image.uploaded_by === currentUserId || isAdmin;
  };

  // 全屏灯箱功能
  const openLightbox = (startIndex: number) => {
    setLightboxState({ images: displayedImages, currentIndex: startIndex });
  };

  const closeLightbox = () => {
    setLightboxState(null);
  };

  const nextImage = () => {
    if (!lightboxState) return;
    setLightboxState({
      ...lightboxState,
      currentIndex: (lightboxState.currentIndex + 1) % lightboxState.images.length
    });
  };

  const prevImage = () => {
    if (!lightboxState) return;
    setLightboxState({
      ...lightboxState,
      currentIndex: (lightboxState.currentIndex - 1 + lightboxState.images.length) % lightboxState.images.length
    });
  };

  const handleSelectFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUserId || !onUpdate) return;

    try {
      setUploading(true);

      const form = new FormData();
      form.append('file', file);
      form.append('userId', currentUserId);
      form.append('cardId', cardId);
      form.append('folder', 'gallery');

      const resp = await fetch(CARD_IMAGE_UPLOAD_ENDPOINT, {
        method: 'POST',
        body: form,
        mode: 'cors',
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Upload failed: ${resp.status} ${text}`);
      }

      const result = await resp.json();
      if (!result.success || !result.data) {
        throw new Error('Invalid response from upload worker');
      }

      // 构造新的 gallery item
      const newImage: GalleryImage = {
        url: result.data.url,
        key: result.data.key,
        uploaded_by: currentUserId,
        uploaded_at: result.data.uploadTime || new Date().toISOString(),
        itinerary_id: currentItineraryId || undefined,
      };

      // 追加到现有 gallery
      const updatedGallery = [...gallery, newImage];
      await onUpdate(updatedGallery);

    } catch (err) {
      console.error('Image upload error:', err);
      alert(`上传失败: ${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (image: GalleryImage) => {
    if (!canDelete(image) || !onUpdate || !currentUserId) return;

    const confirmed = confirm('确定要删除这张图片吗？');
    if (!confirmed) return;

    try {
      setDeleting(image.key);

      // 调用 worker DELETE endpoint
      const deleteUrl = new URL(CARD_IMAGE_UPLOAD_ENDPOINT);
      deleteUrl.searchParams.set('key', image.key);
      deleteUrl.searchParams.set('cardId', cardId);
      deleteUrl.searchParams.set('userId', currentUserId);

      const resp = await fetch(deleteUrl.toString(), {
        method: 'DELETE',
        mode: 'cors',
      });

      if (!resp.ok) {
        throw new Error(`Delete failed: ${resp.status}`);
      }

      // 从 gallery 数组中移除
      const updatedGallery = gallery.filter(img => img.key !== image.key);
      await onUpdate(updatedGallery);

    } catch (err) {
      console.error('Image delete error:', err);
      alert(`删除失败: ${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setDeleting(null);
    }
  };

  if (displayedImages.length === 0 && !canUpload) {
    return null;
  }

  return (
    <View style={{ marginTop: 12 }}>
      {/* 标题栏 */}
      <View style={{ 
        flexDirection: 'row', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <Text style={{ 
          fontSize: 14, 
          fontWeight: '600', 
          color: '#666',
        }}>
          {showAllImages ? (labels?.allPhotos || '所有图片') : (labels?.tripPhotos || '行程图片')} ({displayedImages.length})
        </Text>
        
        {canUpload && currentUserId && (
          <TouchableOpacity
            onPress={handleSelectFile}
            disabled={uploading}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: 12,
              paddingVertical: 6,
              backgroundColor: uploading ? '#ccc' : '#FF6B00',
              borderRadius: 4,
            }}
          >
            {uploading ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <>
                <MdAddPhotoAlternate size={16} color="#fff" />
                <Text style={{ color: '#fff', fontSize: 13, marginLeft: 4 }}>
                  {labels?.addPhoto || '添加图片'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        )}
      </View>

      {/* 图片网格 */}
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={{ marginHorizontal: -4 }}
      >
        {displayedImages.map((img, idx) => (
          <View 
            key={img.key || idx}
            style={{
              marginHorizontal: 4,
              position: 'relative',
            }}
          >
            <TouchableOpacity
              onPress={() => openLightbox(idx)}
              style={{
                width: 120,
                height: 120,
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: '#f0f0f0',
                cursor: 'pointer',
              }}
            >
              <Image
                source={{ uri: img.url }}
                style={{ width: '100%', height: '100%' }}
                resizeMode="cover"
              />
            </TouchableOpacity>

            {/* 删除按钮 */}
            {canDelete(img) && (
              <TouchableOpacity
                onPress={() => handleDelete(img)}
                disabled={deleting === img.key}
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  borderRadius: 12,
                  padding: 4,
                }}
              >
                {deleting === img.key ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <MdDelete size={16} color="#fff" />
                )}
              </TouchableOpacity>
            )}
          </View>
        ))}
      </ScrollView>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/avif"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* 全屏灯箱 - 使用 Portal 渲染到 body */}
      {lightboxState && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={closeLightbox}
        >
          <img
            src={lightboxState.images[lightboxState.currentIndex].url}
            alt=""
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* 关闭按钮 */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#000',
              fontSize: 20,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            <MdClose size={24} />
          </button>
          
          {/* 图片计数器 */}
          {lightboxState.images.length > 1 && (
            <div
              style={{
                position: 'absolute',
                top: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: 20,
                fontSize: 14,
                fontWeight: 'bold',
              }}
            >
              {lightboxState.currentIndex + 1} / {lightboxState.images.length}
            </div>
          )}
          
          {/* 左右切换按钮 */}
          {lightboxState.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                style={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#000',
                  fontSize: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                }}
              >
                <MdChevronLeft size={32} />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                style={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#000',
                  fontSize: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                }}
              >
                <MdChevronRight size={32} />
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </View>
  );
};

export default CardImageGallery;
